"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Book Appointment",
  description: "Schedule your StemWave consultation online in seconds. Our streamlined system ensures your appointment is confirmed instantly.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638797/img/image_r6lmay7m.png"
}, {
  number: "02",
  title: "Clinic Arrival",
  description: "Experience a warm welcome in our modern facility. We prioritize minimal wait times for your StemWave therapy sessions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638797/img/image_m3ewa54q.webp"
}, {
  number: "03",
  title: "StemWave Treatment",
  description: "Receive advanced acoustic wave therapy from our expert providers. This non-invasive treatment targets the source of your pain.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638797/img/image_js8hdi5r.jpg"
}, {
  number: "04",
  title: "Follow-up Support",
  description: "Your recovery journey continues with clear post-treatment instructions and dedicated support to ensure long-term wellness.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638797/img/image_pwf5eti6.jpg"
}];
function StepCard({
  step,
  align
}: {
  step: typeof steps[0];
  align: 'left' | 'right';
}) {
  return <div data-section-id="223307" className={`flex flex-col gap-6 w-full max-w-[400px] ${align === 'right' ? 'md:items-end md:text-right' : 'md:items-start md:text-left'} items-start text-left`}>
      <div className="relative overflow-hidden rounded-2xl border border-border/60 shadow-lg group-hover:border-ring/40 transition-colors duration-500 bg-card">
        <img src={step.image} alt={step.title} className="w-[200px] h-[140px] md:w-[320px] md:h-[220px] object-cover transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent opacity-40 mix-blend-multiply pointer-events-none" />
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-ring font-mono text-xs font-semibold tracking-[0.2em] uppercase flex items-center gap-2">
          {align === 'right' && <span className="w-8 h-px bg-ring/40 hidden md:block" />}
          Step {step.number}
          {align === 'left' && <span className="w-8 h-px bg-ring/40 hidden md:block" />}
        </div>
        <h3 className="font-default text-2xl md:text-3xl font-bold text-primary tracking-tight">{step.title}</h3>
        <p className="font-sans text-muted-foreground text-sm md:text-base leading-relaxed max-w-[320px]">{step.description}</p>
      </div>
    </div>;
}
function StepItem({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const stepNumber = index + 1;
  const isEven = stepNumber % 2 === 0;
  const isOdd = !isEven;
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.6
  });
  return <div data-index={index} className="relative flex flex-col md:flex-row items-center justify-center w-full md:py-16 group">
      
      <div className="md:hidden absolute left-[23px] top-10 -translate-x-1/2 w-5 h-5 rounded-full bg-background border border-ring/50 z-10 flex items-center justify-center shadow-sm">
        <div className="w-2 h-2 rounded-full bg-ring shadow-[0_0_8px_var(--color-ring)]" />
      </div>

      <div className={`w-full md:w-1/2 hidden md:flex ${isEven ? 'md:justify-end md:pr-20' : 'md:justify-end md:opacity-0 md:pointer-events-none'}`}>
        {isEven && <StepCard step={step} align="right" />}
      </div>

      <div ref={ref} className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center z-10">
        <div className="w-8 h-8 rounded-full border border-ring/30 bg-background flex items-center justify-center backdrop-blur-sm">
          <div className={`w-2.5 h-2.5 rounded-full bg-ring transition-all duration-1000 ease-out ${isVisible ? "scale-100 opacity-100 shadow-[0_0_15px_var(--color-ring)]" : "scale-0 opacity-0"}`} />
        </div>
      </div>

      <div className={`w-full md:w-1/2 hidden md:flex ${isOdd ? 'md:justify-start md:pl-20' : 'md:justify-start md:opacity-0 md:pointer-events-none'}`}>
        {isOdd && <StepCard step={step} align="left" />}
      </div>

      <div className="md:hidden pl-14 w-full py-6">
        <StepCard step={step} align="left" />
      </div>
    </div>;
}
export default function TheDirectorsPath() {
  return <section id="the-patient-journey" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-ring/5 rounded-full blur-[120px] pointer-events-none -translate-x-1/2" />
      <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-ring/5 rounded-full blur-[100px] pointer-events-none translate-x-1/2" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 tracking-tight">
            StemWave Treatment Journey
          </h2>
          <p className="font-sans text-muted-foreground text-lg md:text-xl leading-relaxed">
            Our specialized StemWave Acoustic Wave Therapy process is designed to provide non-invasive relief and accelerate recovery through advanced tissue regeneration technology.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div className="absolute left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-ring/30 to-transparent -translate-x-1/2 hidden md:block" />
          
          <div className="absolute left-[23px] top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-ring/30 to-transparent md:hidden" />

          <div className="flex flex-col gap-8 md:gap-0">
            {steps.map((step, i) => <StepItem key={i} step={step} index={i} />)}
          </div>
        </div>

      </div>
    </section>;
}